/**
 * CR283: Hook for Merchant Directory (reads from views, writes via RPCs)
 * UI reads ONLY from v_merchants_directory, v_merchants_pending_count, v_merchants_duplicates
 * UI writes ONLY via RPCs (no direct table updates)
 */

import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Types matching v_merchants_directory view
export interface MerchantDirectoryRow {
  merchant_uuid: string;
  merchant_key: string;
  display_name: string;
  category: string;
  merchant_type: string;
  directory_status: "pending" | "active" | "hidden" | "deleted";
  updated_at: string;
  logo_url: string | null;
  logo_svg: string | null;
  logo_source: string | null;
  logo_attribution: string | null;
  logo_updated_at: string | null;
  has_logo: boolean;
  sources_count: number;
  active_sources_count: number;
  scan_enabled: boolean;
  // Legacy fields (display only)
  legacy_scan_enabled: boolean;
  legacy_is_hidden: boolean;
  legacy_review_state: string;
  // Mismatch flags
  scan_enabled_mismatch: boolean;
  hidden_mismatch: boolean;
  // CR567: Delete lifecycle
  deleted_at?: string | null;
  deleted_by?: string | null;
  delete_reason?: string | null;
  previous_status?: "pending" | "active" | "hidden" | null;
}

export interface MerchantDuplicate {
  normalized_name: string;
  cnt: number;
  merchant_keys: string[];
  display_names: string[];
}

export interface MerchantSource {
  id: string;
  source_label: string;
  source_url: string;
  is_active: boolean;
  status: string;
  priority: number;
  updated_at: string;
}

export interface MerchantAlias {
  id: string;
  alias: string;
  alias_normalized: string;
  alias_type: string;
  priority: number;
  updated_at: string;
}

export interface AuditLogEntry {
  created_at: string;
  event_type: string;
  actor_email: string | null;
  actor_user_id: string | null;
  details: Record<string, unknown>;
}

// Query keys
const DIRECTORY_KEY = ["merchants-directory"];
const PENDING_COUNT_KEY = ["merchants-pending-count"];
const DUPLICATES_KEY = ["merchants-duplicates"];

/**
 * Hook for fetching pending count (for nav badge)
 */
export function useMerchantsPendingCount() {
  return useQuery({
    queryKey: PENDING_COUNT_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_merchants_pending_count")
        .select("pending_count")
        .single();
      
      if (error) throw error;
      return (data?.pending_count as number) ?? 0;
    },
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook for fetching directory list filtered by status
 */
export function useMerchantsDirectory(
  status?: "pending" | "active" | "hidden" | "deleted",
  options?: { scanEnabled?: boolean }
) {
  return useQuery({
    queryKey: [...DIRECTORY_KEY, status, options?.scanEnabled],
    queryFn: async () => {
      let query = supabase
        .from("v_merchants_directory")
        .select("*")
        .order("updated_at", { ascending: false });

      if (status) {
        query = query.eq("directory_status", status);
      }
      if (status === "active" && typeof options?.scanEnabled === "boolean") {
        query = query.eq("scan_enabled", options.scanEnabled);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as MerchantDirectoryRow[];
    },
  });
}

/**
 * Hook for fetching duplicates
 */
export function useMerchantsDuplicates() {
  return useQuery({
    queryKey: DUPLICATES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_merchants_duplicates")
        .select("*")
        .order("cnt", { ascending: false });

      if (error) throw error;
      return (data ?? []) as MerchantDuplicate[];
    },
  });
}

/**
 * Hook for fetching a single merchant by key
 */
export function useMerchantDetail(merchantKey: string | undefined) {
  return useQuery({
    queryKey: [...DIRECTORY_KEY, "detail", merchantKey],
    queryFn: async () => {
      if (!merchantKey) return null;
      
      const { data, error } = await supabase
        .from("v_merchants_directory")
        .select("*")
        .eq("merchant_key", merchantKey)
        .single();

      if (error) throw error;
      return data as MerchantDirectoryRow;
    },
    enabled: !!merchantKey,
  });
}

/**
 * Hook for fetching sources for a merchant
 */
export function useMerchantSources(merchantKey: string | undefined) {
  return useQuery({
    queryKey: ["merchant-sources", merchantKey],
    queryFn: async () => {
      if (!merchantKey) return [];
      
      const { data, error } = await supabase
        .from("merchant_sources")
        .select("id, source_label, source_url, is_active, status, priority, updated_at")
        .eq("merchant_id", merchantKey)
        .order("priority", { ascending: true })
        .order("updated_at", { ascending: false });

      if (error) throw error;
      return (data ?? []) as MerchantSource[];
    },
    enabled: !!merchantKey,
  });
}

/**
 * Hook for fetching aliases for a merchant
 */
export function useMerchantDirectoryAliases(merchantKey: string | undefined) {
  return useQuery({
    queryKey: ["merchant-aliases", merchantKey],
    queryFn: async () => {
      if (!merchantKey) return [];
      
      const { data, error } = await supabase
        .from("merchant_aliases")
        .select("id, alias, alias_normalized, alias_type, priority, updated_at")
        .eq("merchant_id", merchantKey)
        .order("priority", { ascending: true })
        .order("alias");

      if (error) throw error;
      return (data ?? []) as MerchantAlias[];
    },
    enabled: !!merchantKey,
  });
}

/**
 * Hook for fetching audit log for a merchant
 */
export function useMerchantAuditLog(merchantKey: string | undefined) {
  return useQuery({
    queryKey: ["merchant-audit-log", merchantKey],
    queryFn: async () => {
      if (!merchantKey) return [];
      
      const { data, error } = await supabase
        .from("merchant_admin_audit_log")
        .select("created_at, event_type, actor_email, actor_user_id, details")
        .eq("merchant_key", merchantKey)
        .order("created_at", { ascending: false })
        .limit(25);

      if (error) throw error;
      return (data ?? []) as AuditLogEntry[];
    },
    enabled: !!merchantKey,
  });
}

/**
 * Hook providing all directory mutations (RPCs)
 */
export function useMerchantDirectoryMutations() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMissingRpcError = (message?: string | null) =>
    !!message && /Could not find the function public\./i.test(message);

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: DIRECTORY_KEY });
    queryClient.invalidateQueries({ queryKey: PENDING_COUNT_KEY });
    queryClient.invalidateQueries({ queryKey: DUPLICATES_KEY });
  }, [queryClient]);

  // Set status (activate/hide/unhide)
  const setStatusMutation = useMutation({
    mutationFn: async ({ merchantKey, action }: { merchantKey: string; action: "activate" | "hide" | "unhide" }) => {
      const { error } = await supabase.rpc("rpc_set_merchant_status", {
        p_merchant_key: merchantKey,
        p_action: action,
      });
      if (error) throw error;
    },
    onSuccess: (_, { action }) => {
      toast({
        title: "Status updated",
        description: `Merchant ${action === "activate" ? "activated" : action === "hide" ? "hidden" : "unhidden"} successfully`,
      });
      invalidateAll();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message || "Failed to update status",
        variant: "destructive",
      });
    },
  });

  // CR567: Soft delete merchant
  const softDeleteMutation = useMutation({
    mutationFn: async ({ merchantKey, reason }: { merchantKey: string; reason?: string }) => {
      const softDeleteResult = await supabase.rpc("rpc_soft_delete_merchant" as any, {
        p_merchant_key: merchantKey,
        p_reason: reason ?? null,
      });
      if (!softDeleteResult.error) return { mode: "deleted" as const };

      // Backward-compatible fallback when CR567 RPCs are not yet deployed.
      if (isMissingRpcError(softDeleteResult.error.message)) {
        const { error: fallbackError } = await supabase.rpc("rpc_set_merchant_status", {
          p_merchant_key: merchantKey,
          p_action: "hide",
        });
        if (fallbackError) throw fallbackError;
        return { mode: "hidden_fallback" as const };
      }

      throw softDeleteResult.error;
    },
    onSuccess: (result) => {
      if (result?.mode === "hidden_fallback") {
        toast({
          title: "Delete completed (legacy mode)",
          description: "Soft-delete RPC is not deployed yet; merchant was hidden as a fallback.",
        });
      } else {
        toast({
          title: "Merchant deleted",
          description: "Merchant was moved to Deleted.",
        });
      }
      invalidateAll();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message || "Failed to delete merchant",
        variant: "destructive",
      });
    },
  });

  // CR567: Restore soft-deleted merchant
  const restoreMutation = useMutation({
    mutationFn: async ({ merchantKey }: { merchantKey: string }) => {
      const { error } = await supabase.rpc("rpc_restore_merchant" as any, {
        p_merchant_key: merchantKey,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Merchant restored",
        description: "Merchant was restored from Deleted.",
      });
      invalidateAll();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message || "Failed to restore merchant",
        variant: "destructive",
      });
    },
  });

  // CR567: Hard delete merchant (only when soft deleted)
  const eliminateMutation = useMutation({
    mutationFn: async ({ merchantKey }: { merchantKey: string }) => {
      const { error } = await supabase.rpc("rpc_eliminate_merchant" as any, {
        p_merchant_key: merchantKey,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Merchant eliminated",
        description: "Merchant was permanently removed.",
      });
      invalidateAll();
    },
    onError: (err: Error) => {
      toast({
        title: "Eliminate failed",
        description: err.message || "Failed to eliminate merchant",
        variant: "destructive",
      });
    },
  });

  // Toggle scan enabled (updates all sources)
  const setScanEnabledMutation = useMutation({
    onMutate: async ({ merchantKey, enabled }: { merchantKey: string; enabled: boolean }) => {
      await queryClient.cancelQueries({ queryKey: DIRECTORY_KEY });

      const snapshot = queryClient.getQueriesData<MerchantDirectoryRow[]>({
        queryKey: DIRECTORY_KEY,
      });

      for (const [key, rows] of snapshot) {
        if (!rows) continue;
        queryClient.setQueryData<MerchantDirectoryRow[]>(key, (current) => {
          if (!current) return current;
          return current.map((row) =>
            row.merchant_key === merchantKey
              ? { ...row, scan_enabled: enabled, updated_at: new Date().toISOString() }
              : row
          );
        });
      }

      return { snapshot };
    },
    mutationFn: async ({ merchantKey, enabled }: { merchantKey: string; enabled: boolean }) => {
      const { error } = await supabase.rpc("rpc_set_merchant_scan_enabled", {
        p_merchant_key: merchantKey,
        p_enabled: enabled,
      });
      if (error) throw error;
    },
    onSuccess: (_, { enabled }) => {
      toast({
        title: "Scan updated",
        description: `Scanning ${enabled ? "enabled" : "disabled"} for all sources`,
      });
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: ["merchant-sources"] });
    },
    onError: (err: Error, _vars, context) => {
      if (context?.snapshot) {
        for (const [key, rows] of context.snapshot) {
          queryClient.setQueryData(key, rows);
        }
      }
      toast({
        title: "Error",
        description: err.message || "Failed to update scan status",
        variant: "destructive",
      });
    },
  });

  // Resync legacy flags
  const resyncLegacyMutation = useMutation({
    mutationFn: async (merchantKey: string) => {
      const { data, error } = await supabase.rpc("rpc_resync_merchant_legacy_flags", {
        p_merchant_key: merchantKey,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      const changed = Array.isArray(data) && data[0]?.changed;
      toast({
        title: changed ? "Legacy flags resynced" : "No changes needed",
        description: changed ? "Legacy columns updated to match computed values" : "Legacy flags already in sync",
      });
      invalidateAll();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message || "Failed to resync legacy flags",
        variant: "destructive",
      });
    },
  });

  // Update merchant details (overview + branding)
  const updateDetailsMutation = useMutation({
    mutationFn: async ({
      merchantKey,
      overview,
      branding,
    }: {
      merchantKey: string;
      overview?: Record<string, string | null | boolean | number>;
      branding?: Record<string, string | null>;
    }) => {
      const { error } = await supabase.rpc("rpc_update_merchant_details", {
        p_merchant_key: merchantKey,
        p_overview: (overview ?? {}) as unknown as Record<string, never>,
        p_branding: (branding ?? {}) as unknown as Record<string, never>,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Saved",
        description: "Merchant details updated successfully",
      });
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: ["merchant-audit-log"] });
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message || "Failed to update merchant details",
        variant: "destructive",
      });
    },
  });

  // Add alias
  const addAliasMutation = useMutation({
    mutationFn: async ({
      merchantKey,
      alias,
      priority = 0,
      aliasType = "manual",
    }: {
      merchantKey: string;
      alias: string;
      priority?: number;
      aliasType?: string;
    }) => {
      const { error } = await supabase.rpc("rpc_add_merchant_alias", {
        p_merchant_key: merchantKey,
        p_alias: alias,
        p_priority: priority,
        p_alias_type: aliasType,
      });
      if (error) throw error;
    },
    onSuccess: (_, { merchantKey }) => {
      toast({
        title: "Alias added",
        description: "New alias created successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["merchant-aliases", merchantKey] });
      queryClient.invalidateQueries({ queryKey: ["merchant-audit-log", merchantKey] });
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message || "Failed to add alias",
        variant: "destructive",
      });
    },
  });

  // Delete alias
  const deleteAliasMutation = useMutation({
    mutationFn: async ({ aliasId, merchantKey }: { aliasId: string; merchantKey: string }) => {
      const { error } = await supabase.rpc("rpc_delete_merchant_alias", {
        p_alias_id: aliasId,
      });
      if (error) throw error;
      return merchantKey;
    },
    onSuccess: (merchantKey) => {
      toast({
        title: "Alias deleted",
        description: "Alias removed successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["merchant-aliases", merchantKey] });
      queryClient.invalidateQueries({ queryKey: ["merchant-audit-log", merchantKey] });
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message || "Failed to delete alias",
        variant: "destructive",
      });
    },
  });

  return {
    setStatus: setStatusMutation.mutateAsync,
    isSettingStatus: setStatusMutation.isPending,
    setScanEnabled: setScanEnabledMutation.mutateAsync,
    isSettingScan: setScanEnabledMutation.isPending,
    resyncLegacy: resyncLegacyMutation.mutateAsync,
    isResyncing: resyncLegacyMutation.isPending,
    updateDetails: updateDetailsMutation.mutateAsync,
    isUpdatingDetails: updateDetailsMutation.isPending,
    addAlias: addAliasMutation.mutateAsync,
    isAddingAlias: addAliasMutation.isPending,
    deleteAlias: deleteAliasMutation.mutateAsync,
    isDeletingAlias: deleteAliasMutation.isPending,
    softDelete: softDeleteMutation.mutateAsync,
    isSoftDeleting: softDeleteMutation.isPending,
    restoreMerchant: restoreMutation.mutateAsync,
    isRestoringMerchant: restoreMutation.isPending,
    eliminateMerchant: eliminateMutation.mutateAsync,
    isEliminatingMerchant: eliminateMutation.isPending,
  };
}
